import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { MY_ACTIVITY_PAGE, getFindPageUrl } from 'constants/pages';
import {
  CreatePlayRequestPayload,
  CreatePlayRequestResponse,
  MAX_PLAY_REQUEST_MESSAGE_LENGTH,
  PLAY_REQUESTS_API,
} from 'constants/payloads/playRequests';
import { getNextWeekdayDate } from 'utils/shared/date/getNextWeekdayDate';
import { weekdayIndex } from 'utils/shared/date/weekdayIndex';
import { formatClockRange } from 'utils/shared/time/formatVenueTimeRange';
import { splitIntoHourChunks } from 'utils/shared/time/splitIntoHourChunks';
import { useApiGateway } from 'hooks/useApi';
import PaperPlane from 'svg/PaperPlane';
import type { FindPlayer } from 'screens/Find/types';
import { FindTab } from 'screens/Find/types';
import { Button } from 'components/Button';
import { DateRail } from 'components/DateColumn';
import LocationCard from 'components/Find/LocationCard';
import { NeighborhoodOptionRow } from 'components/Find/Neighborhoods';
import OptionTile from 'components/Find/OptionTile';
import { PlayerHeader } from 'components/Find/PlayerDetails';
import Success3dIllustration from 'components/Find/Success3dIllustration';
import { DetailSection } from 'components/Find/detail';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';
import CloseButton from './CloseButton';
import RemainingRequestsAlert from './RemainingRequestsAlert';
import RequestLimitModal from './RequestLimitModal';
import { useRemainingPlayRequests } from './useRemainingPlayRequests';

/** How far ahead the day picker offers dates — two occurrences of a weekly slot. */
const DAYS_AHEAD = 14;

interface Props {
  player: FindPlayer | null;
  isOpen: boolean;
  onClose: () => void;
}

interface DayOption {
  /** Local-midnight date the tile represents. */
  date: Date;
  dayIndex: number;
}

/** One-hour chunk of a weekly slot — what the request actually asks for. */
interface TimeOption {
  key: string;
  availabilitySlotId: string;
  /** "HH:mm" wall-clock at the venue. */
  startTime: string;
  endTime: string;
  label: string;
}

/**
 * The "Request to play" form for a player's availability (Figma "Request"): pick a
 * day from the slots' next occurrences, a one-hour chunk of that day's slot, one
 * court, add a note, submit. The picked hour is sent as a concrete date + time so
 * the host sees exactly when; then a success step with the remaining-requests quota.
 */
export default function PlayerRequestModal({ player, isOpen, onClose }: Props) {
  const router = useRouter();
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [selectedDay, setSelectedDay] = useState<DayOption | null>(null);
  const [selectedTimeKey, setSelectedTimeKey] = useState<string | null>(null);
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);
  const [selectedNeighborhoodIds, setSelectedNeighborhoodIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [remainingAfterSend, setRemainingAfterSend] = useState<number | null>(null);

  const { post, isLoading } = useApiGateway<CreatePlayRequestPayload, CreatePlayRequestResponse>(
    PLAY_REQUESTS_API,
  );
  const { remaining, refetch: refetchRemaining } = useRemainingPlayRequests({ skip: !isOpen });
  const isOutOfRequests = remaining === 0;

  const venues = useMemo(() => player?.requestVenues ?? [], [player]);
  const neighborhoods = useMemo(() => player?.neighborhoods ?? [], [player]);
  const rawSlots = useMemo(() => player?.slots ?? [], [player]);

  // Every date in the next two weeks that one of the weekly slots falls on.
  const dayOptions = useMemo<DayOption[]>(() => {
    const dayIndexes = Array.from(new Set(rawSlots.map((slot) => weekdayIndex(slot.dayOfWeek))));
    const options = dayIndexes.flatMap((dayIndex) => {
      const first = getNextWeekdayDate(dayIndex, player?.timezone);
      const second = new Date(first);
      second.setDate(second.getDate() + 7);
      return [first, second].map((date) => ({ date, dayIndex }));
    });
    const limit = new Date();
    limit.setDate(limit.getDate() + DAYS_AHEAD);
    return options
      .filter((option) => option.date <= limit)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [rawSlots, player?.timezone]);

  // Hour chunks of every slot on a weekday, keyed so the same hour of two slots stays distinct.
  const timeOptionsByDay = useMemo(() => {
    const byDay = new Map<number, TimeOption[]>();
    rawSlots.forEach((slot) => {
      const dayIndex = weekdayIndex(slot.dayOfWeek);
      const chunks = splitIntoHourChunks(slot.startTime, slot.endTime).map((chunk) => ({
        key: `${slot.id}-${chunk.start}`,
        availabilitySlotId: slot.id,
        startTime: chunk.start,
        endTime: chunk.end,
        label: formatClockRange(chunk.start, chunk.end),
      }));
      byDay.set(dayIndex, [...(byDay.get(dayIndex) ?? []), ...chunks]);
    });
    return byDay;
  }, [rawSlots]);

  const timeOptions = selectedDay ? timeOptionsByDay.get(selectedDay.dayIndex) ?? [] : [];
  const selectedTime = timeOptions.find((option) => option.key === selectedTimeKey) ?? null;

  // Defaults on open: the soonest day, its first hour, and the only court if there is one.
  useEffect(() => {
    if (!isOpen) return;
    const firstDay = dayOptions[0] ?? null;
    setSelectedDay(firstDay);
    setSelectedTimeKey(firstDay ? timeOptionsByDay.get(firstDay.dayIndex)?.[0]?.key ?? null : null);
    setSelectedVenueId(venues.length === 1 ? venues[0].id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, player?.id]);

  const handleSelectDay = (option: DayOption) => {
    setSelectedDay(option);
    setSelectedTimeKey(timeOptionsByDay.get(option.dayIndex)?.[0]?.key ?? null);
  };

  // Safe to reset synchronously: Modal renders a snapshot of its children through
  // the exit transition, so the wiped form never shows on the way out.
  const reset = () => {
    setStep('form');
    setSelectedDay(null);
    setSelectedTimeKey(null);
    setSelectedVenueId(null);
    setSelectedNeighborhoodIds([]);
    setMessage('');
    setRemainingAfterSend(null);
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  // A time is required; a place too when the player listed any (a court, or a
  // neighborhood). The button stays enabled so a premature tap explains what's
  // missing instead of going dead.
  const getMissingSelectionMessage = (): string | null => {
    const needsTime = !selectedDay || !selectedTime;
    const hasPlaces = venues.length > 0 || neighborhoods.length > 0;
    const needsPlace = hasPlaces && !selectedVenueId && selectedNeighborhoodIds.length === 0;
    const placeLabel =
      venues.length === 0 ? 'a neighborhood' : neighborhoods.length === 0 ? 'a court' : 'a place';
    const missing = [needsTime && 'a time', needsPlace && placeLabel].filter(Boolean);
    return missing.length > 0 ? `Pick ${missing.join(' and ')} to continue.` : null;
  };

  const toggleNeighborhood = (id: string) =>
    setSelectedNeighborhoodIds((previous) =>
      previous.includes(id) ? previous.filter((value) => value !== id) : [...previous, id],
    );

  const handleSubmit = async () => {
    if (!player?.targetUserId) return;
    const missingSelectionMessage = getMissingSelectionMessage();
    if (missingSelectionMessage || !selectedDay || !selectedTime) {
      toast.error(missingSelectionMessage ?? 'Pick a time to continue.');
      return;
    }
    const response = await post({
      payload: {
        availabilityId: player.id,
        recipientUserId: player.targetUserId,
        requestMessage: message.trim() || undefined,
        availabilitySlotIds: [selectedTime.availabilitySlotId],
        requestedSlot: {
          availabilitySlotId: selectedTime.availabilitySlotId,
          date: format(selectedDay.date, 'yyyy-MM-dd'),
          startTime: selectedTime.startTime,
          endTime: selectedTime.endTime,
        },
        selectedVenueId: selectedVenueId ?? undefined,
        selectedVenueIds: selectedVenueId ? [selectedVenueId] : [],
        selectedNeighborhoodIds,
      },
    });
    if (response.isError) {
      toast.error(response.error || 'Could not send your request');
      return;
    }
    refetchRemaining();
    // The success step reads the server's count; the refetch above may still be in flight.
    const body = response.data as CreatePlayRequestResponse | undefined;
    setRemainingAfterSend(body?.requestsRemaining ?? null);
    setStep('success');
  };

  // A capped viewer gets Figma's limit modal instead of a form they couldn't
  // submit. Never over the success step — sending the last allowed request drops
  // `remaining` to zero on the way into it.
  if (step === 'form' && isOutOfRequests) {
    return <RequestLimitModal isOpen={isOpen} onClose={handleClose} />;
  }

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      classNameMaxWidth="max-w-md lg:max-w-[40rem]"
      classNameRounded="rounded-t-3xl sm:rounded-2xl lg:rounded-3xl"
      classNameHeight="max-h-[90dvh]"
    >
      <div className="flex max-h-[90dvh] flex-col bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary">
        {step === 'form' ? (
          <>
            <header className="flex shrink-0 items-center justify-between border-b border-color-border-input-lightmode p-ds-lg dark:border-color-border-input-darkmode lg:px-ds-3xl lg:py-5">
              <h2 className="typography-product-heading-mobile lg:typography-product-heading-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Request to play
              </h2>
              <CloseButton onClose={handleClose} />
            </header>

            <div className="flex min-h-0 flex-1 flex-col gap-ds-2xl overflow-y-auto p-ds-lg lg:p-ds-3xl">
              {player && (
                <div className="rounded-[1.25rem] border border-color-border-input-lightmode bg-color-bg-lightmode-primary p-ds-lg shadow-[0px_4px_10px_0px_rgba(0,0,0,0.16)] dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary">
                  <PlayerHeader player={player} sizeDesktop="md" />
                </div>
              )}

              <RemainingRequestsAlert remaining={remaining} />

              {dayOptions.length > 0 && (
                <DetailSection title="Select day" tone="secondary" sizeDesktop="lg">
                  {/* py/-my pair: the row scrolls, so it would clip the selected tile's shadow. */}
                  <div className="no-scrollbar -my-ds-md flex gap-ds-md overflow-x-auto py-ds-md">
                    {dayOptions.map((option) => (
                      <OptionTile
                        key={option.date.toISOString()}
                        selected={selectedDay?.date.getTime() === option.date.getTime()}
                        onClick={() => handleSelectDay(option)}
                        className="min-w-[4.5rem] lg:min-w-[8rem]"
                      >
                        <DateRail date={option.date.toISOString()} />
                      </OptionTile>
                    ))}
                  </div>
                </DetailSection>
              )}

              {timeOptions.length > 0 && (
                <DetailSection title="Time" tone="secondary" sizeDesktop="lg">
                  <div className="no-scrollbar -my-ds-md flex gap-ds-md overflow-x-auto py-ds-md">
                    {timeOptions.map((option) => {
                      const isSelected = selectedTimeKey === option.key;
                      return (
                        <OptionTile
                          key={option.key}
                          selected={isSelected}
                          onClick={() => setSelectedTimeKey(option.key)}
                        >
                          <span
                            className={classNames(
                              'typography-product-body-highlight whitespace-nowrap',
                              isSelected
                                ? 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary'
                                : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
                            )}
                          >
                            {option.label}
                          </span>
                        </OptionTile>
                      );
                    })}
                  </div>
                </DetailSection>
              )}

              {venues.length > 0 && (
                <DetailSection title="Select court" tone="secondary" sizeDesktop="lg">
                  {/* py/-my pair: this scrolls, so it clips the selected card's ring without it. */}
                  <div className="no-scrollbar -mx-ds-lg -my-ds-md flex gap-ds-lg overflow-x-auto px-ds-lg py-ds-md lg:-mx-ds-3xl lg:px-ds-3xl">
                    {venues.map((venue) => (
                      <LocationCard
                        key={venue.id}
                        venue={venue}
                        selected={selectedVenueId === venue.id}
                        onToggle={() => setSelectedVenueId(venue.id)}
                      />
                    ))}
                  </div>
                </DetailSection>
              )}

              {neighborhoods.length > 0 && (
                <DetailSection title="Select neighborhoods" tone="secondary" sizeDesktop="lg">
                  <div className="flex flex-col gap-ds-md">
                    {neighborhoods.map((neighborhood) => (
                      <NeighborhoodOptionRow
                        key={neighborhood.id}
                        name={neighborhood.name}
                        selected={selectedNeighborhoodIds.includes(neighborhood.id)}
                        onToggle={() => toggleNeighborhood(neighborhood.id)}
                      />
                    ))}
                  </div>
                </DetailSection>
              )}

              <DetailSection title="Say something" tone="secondary" sizeDesktop="lg">
                <textarea
                  aria-label="Say something"
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="Let's play!"
                  rows={3}
                  maxLength={MAX_PLAY_REQUEST_MESSAGE_LENGTH}
                  className="input-form"
                />
              </DetailSection>
            </div>

            <footer className="shrink-0 border-t border-color-border-input-lightmode px-ds-lg pb-ds-lg pt-ds-lg dark:border-color-border-input-darkmode lg:border-t-0 lg:px-ds-3xl lg:pb-ds-3xl lg:pt-0">
              <Button
                variant="brand"
                size="lg"
                onClick={handleSubmit}
                loading={isLoading}
                disabled={!player?.targetUserId}
                iconRight={<PaperPlane className="h-5 w-5" />}
              >
                Submit request
              </Button>
            </footer>
          </>
        ) : (
          <>
            <header className="flex shrink-0 items-center justify-end p-ds-lg lg:px-ds-3xl lg:py-5">
              <CloseButton onClose={handleClose} />
            </header>
            <div className="flex min-h-0 flex-1 flex-col items-center gap-ds-lg overflow-y-auto px-ds-lg pb-ds-lg pt-ds-sm text-center lg:gap-ds-2xl lg:px-ds-3xl lg:pb-16">
              <Success3dIllustration className="h-[7.5rem] w-[7.5rem]" />
              {/* `contents` keeps the mobile column flat; a real text block at lg. */}
              <div className="contents lg:flex lg:w-full lg:flex-col lg:items-center lg:gap-ds-xl">
                <h2 className="typography-product-display text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  Request sent!
                </h2>
                <p className="typography-product-body max-w-[21.5rem] text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary lg:max-w-none">
                  If the player accepts your request, we&rsquo;ll start a chat in the{' '}
                  <strong className="font-bold">Activity</strong> tab.
                </p>
                <RemainingRequestsAlert
                  remaining={remainingAfterSend}
                  className="w-full text-left lg:w-auto"
                />
              </div>
            </div>
            <footer className="flex shrink-0 flex-col gap-ds-md border-t border-color-border-input-lightmode px-ds-lg pb-ds-lg pt-ds-lg dark:border-color-border-input-darkmode lg:flex-row lg:gap-ds-xl lg:px-ds-3xl lg:py-5">
              <Button
                variant="secondary"
                size="lg"
                className="lg:min-w-0 lg:flex-1"
                onClick={() => {
                  handleClose();
                  router.push(MY_ACTIVITY_PAGE);
                }}
              >
                View in <strong className="font-bold">Activity</strong>
              </Button>
              <Button
                variant="primary"
                size="lg"
                className="lg:min-w-0 lg:flex-1"
                onClick={() => {
                  handleClose();
                  router.push(getFindPageUrl(FindTab.Players));
                }}
              >
                Continue browsing
              </Button>
            </footer>
          </>
        )}
      </div>
    </Modal>
  );
}
